// Merged identify pipeline for image-mode ArcGIS layers, shared by the track map (map-base.js)
// and the MapLayer admin preview (map.mjs): instead of esri-leaflet's per-layer bindPopup —
// whose several map-click identifies race to open competing popups — each active image-mode
// overlay registers here, and a single per-map click handler fires one identify per overlay,
// merges every result, and opens ONE popup grouped map layer › sublayer with one Property/Value
// table per feature. Pure helpers (grouping, HTML) are node-tested; the coordinator half needs
// Leaflet (window.L) and is covered by manual QA.

import { htmlEscape } from "./html-escape.mjs";
import { buildFeaturePropertiesTable, buildFeaturePropertiesPopup } from "./feature-properties-popup.mjs";
import { buildFieldPresentation, remapAliasKeyedProperties, substituteFieldTemplate } from "./arcgis-popup-fields.mjs";
import { fetchItemLayerOverrides } from "./arcgis-item-overrides.mjs";
import { fetchSublayerMetadata } from "./arcgis-sublayer-metadata.mjs";

// Shapes settled identify results into display groups. entries: [{ title, featureCollection,
// response, presentations }] in registration order (null entries — failed identifies — are
// skipped). Sublayer names come from the raw identify response's results[].layerName (the
// converted GeoJSON features only carry layerId); unnamed numeric ids display as "#id", and
// features with no numeric layerId gather in a nameless null-id section sorted last. Entries with
// no features are dropped. presentations is an optional Map<layerId, presentation>
// (arcgis-popup-fields.mjs) — each section carries its sublayer's entry (null when absent) so the
// popup builder can label/format its tables.
export function groupIdentifyResults(entries) {
	const groups = [];
	for (const entry of Array.isArray(entries) ? entries : []) {
		if (entry == null || entry.featureCollection == null || !Array.isArray(entry.featureCollection.features)) {
			continue;
		}
		const names = new Map();
		const results = entry.response != null && Array.isArray(entry.response.results) ? entry.response.results : [];
		for (const result of results) {
			if (result != null && typeof result.layerId === "number" && typeof result.layerName === "string" && result.layerName !== "") {
				names.set(result.layerId, result.layerName);
			}
		}
		const sections = new Map();
		for (const feature of entry.featureCollection.features) {
			if (feature == null) {
				continue;
			}
			const layerId = typeof feature.layerId === "number" ? feature.layerId : null;
			let section = sections.get(layerId);
			if (section == null) {
				const layerName = layerId === null ? "" : (names.get(layerId) != null ? names.get(layerId) : `#${layerId}`);
				const presentation = layerId !== null && entry.presentations instanceof Map && entry.presentations.get(layerId) != null
					? entry.presentations.get(layerId)
					: null;
				section = { layerId: layerId, layerName: layerName, presentation: presentation, features: [] };
				sections.set(layerId, section);
			}
			section.features.push(feature.properties != null ? feature.properties : {});
		}
		if (sections.size === 0) {
			continue;
		}
		const ordered = [...sections.values()].sort((a, b) => {
			if (a.layerId === null) {
				return 1;
			}
			if (b.layerId === null) {
				return -1;
			}
			return a.layerId - b.layerId;
		});
		groups.push({ title: typeof entry.title === "string" ? entry.title : "", sections: ordered });
	}
	return groups;
}

// Identify has no server-side result cap (3px tolerance over stacked polygons can intersect
// dozens of features), and the popupopen handler initializes a DataTable per rendered table —
// unbounded, a pathological click freezes the browser for seconds. 25 tables is far beyond what
// anyone reads in a popup and initializes instantly.
const MAX_IDENTIFY_FEATURE_TABLES = 25;

// The merged popup HTML. A single-table result (one group, one section, one feature) is exactly
// buildFeaturePropertiesPopup's output — no heading, search box retained — so the common
// one-feature click looks identical to the pre-merge popup. Multi-table popups put a heading
// above each feature's table ("<map layer Name> › <sublayer>", the map-layer part only when
// several groups are present) and mark the tables compact (sort only, no per-table search).
// When more than one section is present, a jump nav tops the popup: one link per named section
// (data-identify-target) pointing at the section's first heading (data-identify-section), so the
// reader doesn't have to scroll hunting for a layer. wireIdentifyNav attaches the click handlers
// after the popup opens (the markup stays inert HTML — no inline handlers, CSP-safe).
export function buildIdentifyPopupHtml(groups, labels) {
	if (!Array.isArray(groups) || groups.length === 0) {
		return "";
	}
	let featureCount = 0;
	let sectionCount = 0;
	for (const group of groups) {
		sectionCount += group.sections.length;
		for (const section of group.sections) {
			featureCount += section.features.length;
		}
	}
	if (featureCount === 0) {
		return "";
	}
	if (featureCount === 1 && groups.length === 1 && groups[0].sections.length === 1) {
		const only = groups[0].sections[0];
		return buildFeaturePropertiesPopup(remapAliasKeyedProperties(only.features[0], only.presentation), labels, only.presentation);
	}
	let nav = "";
	let body = "";
	let sectionIndex = 0;
	let renderedTables = 0;
	for (const group of groups) {
		for (const section of group.sections) {
			const headingText = groups.length > 1 && group.title !== ""
				? `${group.title} › ${section.layerName}`
				: section.layerName;
			const marker = `s${sectionIndex}`;
			sectionIndex++;
			let isFirstFeature = true;
			for (const rawProperties of section.features) {
				if (renderedTables >= MAX_IDENTIFY_FEATURE_TABLES) {
					break;
				}
				if (headingText !== "") {
					const anchor = isFirstFeature ? ` data-identify-section="${marker}"` : "";
					body += `<div class="identify-heading font-weight-bold"${anchor}>${htmlEscape(headingText)}</div>`;
				}
				isFirstFeature = false;
				const properties = remapAliasKeyedProperties(rawProperties, section.presentation);
				const featureTitle = section.presentation != null
					? substituteFieldTemplate(section.presentation.title, properties, section.presentation.fields)
					: null;
				if (featureTitle != null) {
					body += `<div class="identify-feature-title font-weight-bold">${htmlEscape(featureTitle)}</div>`;
				}
				body += buildFeaturePropertiesTable(properties, labels, true, section.presentation);
				renderedTables++;
			}
			// A section the cap kept entirely out of the popup gets no jump-nav link — the link
			// would scroll to nothing (isFirstFeature only flips once a heading+table rendered).
			if (sectionCount > 1 && headingText !== "" && !isFirstFeature) {
				nav += `<a href="#" class="identify-nav-link" data-identify-target="${marker}">${htmlEscape(headingText)}</a>`;
			}
		}
	}
	const navHtml = nav !== "" ? `<div class="identify-nav">${nav}</div>` : "";
	let note = "";
	if (featureCount > MAX_IDENTIFY_FEATURE_TABLES && typeof labels.showingFirst === "string") {
		note = `<div class="identify-truncation-note text-muted small mt-2">${htmlEscape(
			labels.showingFirst.replace("{0}", String(MAX_IDENTIFY_FEATURE_TABLES)).replace("{1}", String(featureCount)))}</div>`;
	}
	return `<div class="feature-properties-popup standard-ui">${navHtml}${body}${note}</div>`;
}

// Per-sublayer popup presentations for an image-mode overlay: sublayer metadata supplies
// fields/displayField, the service's ArcGIS Online item (when public) supplies the authored
// popupInfo — the same sources feature mode presents from. Resolves Map<layerId, presentation>
// with only the sublayers that yielded one (identify falls back to the raw dump for the rest);
// never rejects, and every fetch behind it is promise-cached, so per-click resolution after the
// first is free. Exported for unit tests.
export function fetchIdentifyPresentations(baseUrl, layerIds) {
	if (typeof baseUrl !== "string" || baseUrl === "" || !Array.isArray(layerIds) || layerIds.length === 0) {
		return Promise.resolve(new Map());
	}
	return Promise.all(layerIds.map((id) => fetchSublayerMetadata(baseUrl, id).then((metadata) => {
		if (metadata == null) {
			return null;
		}
		return fetchItemLayerOverrides(metadata.serviceItemId, baseUrl, id)
			.then((overrides) => [id, buildFieldPresentation(metadata.fields, overrides.popupInfo, metadata.displayField)]);
	}))).then((pairs) => new Map(pairs.filter((pair) => pair != null && pair[1] != null)));
}

// --- Per-map click coordinator. State keyed by the Leaflet map instance (same pattern as the
// legend control) so the preview and track maps never share handlers. A monotonic token guards
// every async render: a newer click or a dblclick (double-click zoom) bumps it, so a stale
// identify can never open a popup. Unregistering a key does NOT bump the token — a still-pending
// merge simply drops that key's entry (registration is re-checked when results settle). ---
const identifyStates = new WeakMap(); // map -> { layers: Map<key, {title, overlay, presentations}>, labels, token, popup, onClick, onDblClick, onDragStart }

// Host-registered click guards: while a map is collecting location input (ruler points, add/send
// position, place picking, geofence drawing), a click is an input, not a question — identify
// stays quiet. Kept outside controlStates' lifecycle so a guard survives the last layer
// unregistering and re-registering.
const identifyGuards = new WeakMap(); // map -> () => boolean

// Registers the predicate consulted before every identify click; truthy = suppress. One guard
// per map (last wins) — the host page owns the semantics of "busy choosing a location".
export function setIdentifyClickGuard(map, guard) {
	identifyGuards.set(map, guard);
}

// One identify request for one registered overlay, mirroring the construction esri-leaflet's own
// _getPopupData performs (simplify unless maxAllowableOffset, visible:<ids>, per-id layerDef
// copies) so the stored sublayer selection and where-filter are honored. Never rejects: any
// failure resolves null and the layer is simply absent from the popup.
function runIdentify(map, latlng, key, entry) {
	return new Promise((resolve) => {
		try {
			const request = entry.overlay.identify().on(map).at(latlng);
			if (!request.params.maxAllowableOffset) {
				request.simplify(map, 0.5);
			}
			const layerIds = entry.overlay.options.layers;
			if (Array.isArray(layerIds) && layerIds.length > 0) {
				request.layers(`visible:${layerIds.join(",")}`);
			} else {
				request.layers("visible");
			}
			const layerDefs = entry.overlay.options.layerDefs;
			if (layerDefs != null && typeof layerDefs !== "string" && !request.params.layerDefs) {
				for (const id in layerDefs) {
					if (Object.prototype.hasOwnProperty.call(layerDefs, id)) {
						request.layerDef(id, layerDefs[id]);
					}
				}
			}
			request.run((error, featureCollection, response) => {
				if (error) {
					resolve(null);
					return;
				}
				// overlay travels with the result so the merge can drop it if the key was
					// re-registered with a different overlay while this identify was in flight.
					resolve({ key: key, title: entry.title, overlay: entry.overlay, featureCollection: featureCollection, response: response });
			});
		} catch (error) {
			resolve(null);
		}
	});
}

function handleIdentifyClick(map, state, event) {
	const guard = identifyGuards.get(map);
	if (guard != null && guard()) {
		return;
	}
	const token = ++state.token;
	const zoom = map.getZoom();
	const pending = [];
	for (const [key, entry] of state.layers) {
		const options = entry.overlay.options;
		// Zoom-gated overlays draw nothing at this zoom; their results would be phantoms.
		if ((options.minZoom != null && zoom < options.minZoom) || (options.maxZoom != null && zoom > options.maxZoom)) {
			continue;
		}
		pending.push(runIdentify(map, event.latlng, key, entry));
	}
	if (pending.length === 0) {
		return;
	}
	Promise.all(pending).then((settled) => {
		if (token !== state.token) {
			return;
		}
		// A result only stays live while its key is still registered with the SAME overlay — a
		// toggle-off/toggle-on during the flight swaps the overlay object, and its old results
		// would be ghosts.
		const live = settled.filter((result) => result != null
			&& state.layers.has(result.key)
			&& state.layers.get(result.key).overlay === result.overlay);
		if (live.length === 0) {
			return;
		}
		// Attach each live entry's per-sublayer presentations (usually already settled — they
		// start resolving at registration). The token is re-checked after the extra await so this
		// never widens the stale-popup window.
		return Promise.all(live.map((result) => {
			const entry = state.layers.get(result.key);
			const presentations = entry != null && entry.presentations != null ? entry.presentations : Promise.resolve(null);
			return presentations.then((resolved) => ({ ...result, presentations: resolved }));
		})).then((withPresentations) => {
			if (token !== state.token) {
				return;
			}
			// Re-check the choose-location guard: the user may have armed the ruler / add-place /
			// send-position tool while the identify was in flight, and a popup would land on top
			// of their point-picking.
			const liveGuard = identifyGuards.get(map);
			if (liveGuard != null && liveGuard()) {
				return;
			}
			// Re-check registration too: unregistering does not bump the token (see the state
			// comment), so a layer toggled off while its presentations were still resolving must
			// drop out here — the pre-await filter can't see it.
			const stillLive = withPresentations.filter((result) => state.layers.has(result.key)
				&& state.layers.get(result.key).overlay === result.overlay);
			if (stillLive.length === 0) {
				return;
			}
			const html = buildIdentifyPopupHtml(groupIdentifyResults(stillLive), state.labels);
			if (html === "") {
				return;
			}
			state.popup = window.L.popup({ maxWidth: 480, minWidth: 340, maxHeight: 320 })
				.setLatLng(event.latlng)
				.setContent(html)
				.openOn(map);
			wireIdentifyNav(state.popup);
		});
	});
}

// Attaches the jump-nav click handlers after the popup opens. Scrolls the popup's own scrollable
// body (.leaflet-popup-content, which Leaflet caps via the maxHeight option) rather than using
// scrollIntoView — the admin form's page can scroll too, and scrollIntoView would drag it along.
function wireIdentifyNav(popup) {
	const element = popup.getElement == null ? null : popup.getElement();
	if (element == null) {
		return;
	}
	const container = element.querySelector(".leaflet-popup-content");
	if (container == null) {
		return;
	}
	for (const link of element.querySelectorAll("a.identify-nav-link")) {
		link.addEventListener("click", (clickEvent) => {
			clickEvent.preventDefault();
			const target = element.querySelector(`[data-identify-section="${link.getAttribute("data-identify-target")}"]`);
			if (target != null) {
				container.scrollTop += target.getBoundingClientRect().top - container.getBoundingClientRect().top;
			}
		});
	}
}

// Registers an image-mode overlay for merged identify on this map. Re-registering a key swaps its
// overlay (the admin preview re-registers every refresh). labels = { property, value,
// showingFirst } — table header strings plus the "Showing the first {0} of {1}" truncation
// template; the last registration's labels win (both surfaces pass one constant set per page).
export function registerIdentifyLayer(map, key, title, overlay, labels) {
	let state = identifyStates.get(map);
	if (state == null) {
		state = { layers: new Map(), token: 0, labels: labels, popup: null, onClick: null, onDblClick: null, onDragStart: null };
		state.onClick = (event) => handleIdentifyClick(map, state, event);
		state.onDblClick = () => {
			state.token++;
			// On a fast service the FIRST click's identify can resolve and open its popup within
			// the double-click gap; the token only guards popups that have not opened yet, so the
			// double-click zoom must also close the one that got through.
			if (state.popup != null && state.popup.isOpen()) {
				map.closePopup(state.popup);
			}
			state.popup = null;
		};
		// Dragging away from the clicked spot makes a still-pending identify stale: without this,
		// a slow service's popup would open seconds later and auto-pan the map back. dragstart —
		// not movestart/zoomstart — because Leaflet fires those for PROGRAMMATIC movement too
		// (live-follow's fitBounds re-centers every few seconds, another popup's autoPan,
		// zoom-to-region), which would silently cancel every identify while following an asset.
		state.onDragStart = () => {
			state.token++;
		};
		map.on("click", state.onClick);
		map.on("dblclick", state.onDblClick);
		map.on("dragstart", state.onDragStart);
		identifyStates.set(map, state);
	}
	if (labels != null) {
		state.labels = labels;
	}
	// Presentations start resolving at registration so they are usually settled before the first
	// click; every fetch behind them is promise-cached, so re-registration costs nothing.
	const options = overlay != null && overlay.options != null ? overlay.options : {};
	state.layers.set(key, {
		title: typeof title === "string" ? title : "",
		overlay: overlay,
		presentations: fetchIdentifyPresentations(options.url, options.layers)
	});
}

// Removes one overlay; the map handlers go with the last registration. An open identify popup
// closes with it: the merged content may show the removed layer's data, and on
// closePopupOnClick:false maps (the admin preview) nothing else could ever dismiss it — the
// rebuilt registration holds a fresh popup: null.
export function unregisterIdentifyLayer(map, key) {
	const state = identifyStates.get(map);
	if (state == null || !state.layers.delete(key)) {
		return;
	}
	if (state.popup != null && state.popup.isOpen()) {
		map.closePopup(state.popup);
	}
	state.popup = null;
	if (state.layers.size === 0) {
		map.off("click", state.onClick);
		map.off("dblclick", state.onDblClick);
		map.off("dragstart", state.onDragStart);
		identifyStates.delete(map);
	}
}
